/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useMemo, useState } from 'react';
import type { FormInstance } from 'antd';
import { Tooltip, Button, Form, Input } from 'antd';
import type { RefObject, SetStateAction } from 'react';
import { DeleteFilled, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { applicationDomainValidator } from 'utils/inputValidators/inputValidators';
import { useTranslation } from 'react-i18next';
import Container from './Container';
import type { InputValidationStatus } from './validators';
import { getInputStatus, domainIsDuplicate } from './validators';
import './styles.less';

interface FormType {
  domains: string[];
}

export interface DomainsInputProps {
  form: RefObject<FormInstance>;
  value?: string[];
  onChange: (inputId: string) => void;
}

const formItemLayout = {
  labelCol: {
    sm: { span: 3 },
  },
  wrapperCol: {
    sm: { span: 21 },
  },
};
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    sm: { span: 21, offset: 3 },
  },
};

const defaultStatus = {
  status: undefined,
  message: undefined,
};

const DomainsInput = ({ form, value, onChange }: DomainsInputProps) => {
  const { t } = useTranslation();
  const [validationStatus, setValidationStatus] = useState<InputValidationStatus[]>(
    value ? value.map(() => defaultStatus) : [],
  );
  const handleFieldsChange = useCallback(() => {
    onChange('azDomains');
  }, [onChange]);

  const handleInputChange = useCallback(() => {
    const values = form.current?.getFieldValue('azDomains') as string[] | undefined;
    const tempStatus: SetStateAction<InputValidationStatus[]> = [];
    if (values) {
      values.forEach((v, i) => {
        const status = getInputStatus(
          v,
          values.filter((_, index) => index !== i),
        );
        tempStatus.push(status);
      });
    }
    setValidationStatus(tempStatus);
  }, [form]);

  const initialValues = useMemo(() => {
    let defaultValue: string[] | undefined;
    if (!value || value.length === 0) {
      defaultValue = [''];
    } else {
      defaultValue = value;
    }

    return {
      azDomains: defaultValue,
    };
  }, [value]);

  return (
    <Container>
      <Form<FormType>
        wrapperCol={formItemLayoutWithOutLabel.wrapperCol}
        ref={form}
        initialValues={initialValues}
        onFieldsChange={handleFieldsChange}
      >
        <Form.List name="azDomains">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => {
                const isLast = index === fields.length - 1;

                const inputStatus = validationStatus[index];
                const asyncValidator = () =>
                  new Promise((resolve, reject) => {
                    const values = form.current?.getFieldValue('azDomains') as string[] | undefined;
                    if (values) {
                      const currentValue = values[index];
                      const otherValues = values.filter((_, i) => i !== index);
                      const isDuplicate = domainIsDuplicate(currentValue, otherValues);

                      if (isDuplicate) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        reject(t('profileInputs.azDomains.duplicateError'));
                      }

                      resolve(true);
                    }
                  });

                return (
                  <Form.Item
                    help={inputStatus?.message}
                    validateStatus={inputStatus?.status}
                    label={`Domain ${index + 1}`}
                    key={field.key}
                    wrapperCol={formItemLayout.wrapperCol}
                    labelCol={formItemLayout.labelCol}
                  >
                    <Form.Item
                      {...field}
                      validateTrigger={['onChange', 'onBlur']}
                      rules={[
                        {
                          required: true,
                          message: t('profileInputs.customAppDomain.requiredError'),
                        },
                        {
                          validator: applicationDomainValidator,
                        },
                        {
                          validator: asyncValidator,
                        },
                      ]}
                      shouldUpdate
                      noStyle
                    >
                      <Input
                        className="domains-input__input"
                        onChange={handleInputChange}
                        prefix={
                          inputStatus?.showWarning ? (
                            <Tooltip title={inputStatus?.warningTooltip}>
                              <WarningOutlined style={{ color: '#FAAD14' }} />
                            </Tooltip>
                          ) : (
                            <span />
                          )
                        }
                        onPressEnter={isLast ? () => add() : undefined}
                        autoFocus={isLast}
                      />
                    </Form.Item>
                    {index !== 0 && (
                      <Button
                        onClick={() => {
                          remove(field.name);
                          handleInputChange();
                        }}
                        icon={<DeleteFilled style={{ color: '#BFBFBF' }} />}
                        className="domains-input__delete-button"
                        type="text"
                      />
                    )}
                  </Form.Item>
                );
              })}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                    handleInputChange();
                  }}
                  icon={<PlusOutlined />}
                  className="domains-input__input"
                >
                  {t('profileInputs.azDomains.addButton')}{' '}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Container>
  );
};

export default DomainsInput;
